import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Heart, Eye, EyeOff, ArrowLeft, Loader2, Check, Church as ChurchIcon } from "lucide-react";
import { apiClient } from "../lib/apiClient";


export default function SignupPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    church_id: "",
  });
  const [churches, setChurches] = useState([]);

  useEffect(() => {
    // Public, unauthenticated endpoint — returns just {id, name}.
    // Silent fallback to empty list so signup keeps working even if Mongo blips.
    apiClient
      .get("churchesPublic")
      .then((res) => setChurches(res.data || []))
      .catch(() => setChurches([]));
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleChurchChange = (value) => {
    // "__none__" is the sentinel for "no church selected" — the dropdown can't
    // hold an empty string value (Radix forbids it). Map it back to "" before
    // sending to the API.
    setFormData({ ...formData, church_id: value === "__none__" ? "" : value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Strip church_id if blank — API treats null/missing as "no church".
      const payload = { ...formData };
      if (!payload.church_id) delete payload.church_id;
      const response = await apiClient.post("authRegister", payload);
      const { access_token, user } = response.data;

      sessionStorage.setItem("auth_token", access_token);
      localStorage.setItem("user", JSON.stringify(user));

      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = () => {
    const { password } = formData;
    if (password.length === 0) return null;
    if (password.length < 6) return { label: "Too short", color: "text-red-500" };
    if (password.length < 8) return { label: "Weak", color: "text-yellow-500" };
    if (password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password)) {
      return { label: "Strong", color: "text-green-500" };
    }
    return { label: "Good", color: "text-sage" };
  };

  const strength = passwordStrength();

  return (
    <div className="min-h-screen bg-stone-light noise-bg flex flex-col">
      {/* Header */}
      <header className="p-4">
        <div className="max-w-6xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-stone-dark/70 hover:text-sage transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to home</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
            </Link>
            <h1 className="font-serif text-2xl font-semibold text-stone-dark mt-4">
              Start your journey
            </h1>
            <p className="text-stone-dark/60 mt-2">
              Create an account to begin tracking your prayers
            </p>
          </div>

          {/* Signup Card */}
          <Card className="border-stone shadow-lg" data-testid="signup-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-serif text-xl text-center">Create Account</CardTitle>
              <CardDescription className="text-center">
                Fill in your details to get started
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div 
                    className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm"
                    data-testid="signup-error"
                  >
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    minLength={2}
                    className="bg-stone-light border-stone focus:border-sage focus:ring-sage/20"
                    data-testid="signup-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-stone-light border-stone focus:border-sage focus:ring-sage/20"
                    data-testid="signup-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={6}
                      className="bg-stone-light border-stone focus:border-sage focus:ring-sage/20 pr-10"
                      data-testid="signup-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-dark/50 hover:text-stone-dark"
                      data-testid="toggle-password"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {strength && (
                    <p className={`text-xs ${strength.color}`} data-testid="password-strength">
                      Password strength: {strength.label}
                    </p>
                  )}
                </div>

                {/* Church (optional) */}
                <div className="space-y-2">
                  <Label htmlFor="church_id" className="text-stone-dark flex items-center gap-2">
                    <ChurchIcon className="w-4 h-4 text-stone-dark/50" />
                    Church <span className="text-stone-dark/50 text-sm font-normal">(optional)</span>
                  </Label>
                  <Select
                    value={formData.church_id || "__none__"}
                    onValueChange={handleChurchChange}
                  >
                    <SelectTrigger
                      id="church_id"
                      className="bg-stone-light border-stone focus:border-sage focus:ring-sage/20"
                      data-testid="signup-church-select"
                    >
                      <SelectValue placeholder="No church (you can pick one later)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" data-testid="signup-church-none">
                        No church (you can pick one later)
                      </SelectItem>
                      {churches.map((c) => (
                        <SelectItem
                          key={c.id}
                          value={c.id}
                          data-testid={`signup-church-option-${c.id}`}
                        >
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Benefits */}
                <div className="bg-sage/5 rounded-xl p-4 space-y-2">
                  <p className="text-sm font-medium text-stone-dark">What you'll get:</p>
                  <ul className="space-y-1">
                    {[
                      "Unlimited prayer tracking",
                      "Create and join prayer groups",
                      "Print prayer lists"
                    ].map((benefit) => (
                      <li key={benefit} className="flex items-center gap-2 text-sm text-stone-dark/70">
                        <Check className="w-4 h-4 text-sage" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-sage hover:bg-sage-dark text-white rounded-full py-5 mt-6 font-medium"
                  disabled={loading}
                  data-testid="signup-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-stone-dark/60 text-sm">
                  Already have an account?{" "}
                  <Link 
                    to="/login" 
                    className="text-sage hover:underline font-medium"
                    data-testid="login-link"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Background decoration */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sage/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>
    </div>
  );
}
